import React, { useState, useEffect } from 'react';
import { Users, X, ArrowRight, Check, AlertTriangle, Loader2, Search } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useNotifications } from '../context/NotificationContext';
import './MergePatientsModal.css';

const MergePatientsModal = ({ isOpen, onClose, onMergeSuccess }) => {
  const { addNotification } = useNotifications();
  const [step, setStep] = useState(1);

  // Patient states
  const [searchSource, setSearchSource] = useState('');
  const [sourceResults, setSourceResults] = useState([]);
  const [sourcePatient, setSourcePatient] = useState(null);

  const [searchDest, setSearchDest] = useState('');
  const [destResults, setDestResults] = useState([]);
  const [destinationPatient, setDestinationPatient] = useState(null);

  // Status states
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [merging, setMerging] = useState(false);
  const [finished, setFinished] = useState(false);

  // Stats for confirmation step
  const [stats, setStats] = useState({ appointments: 0, evolutions: 0 });

  useEffect(() => {
    if (!isOpen) {
      // Reset state on close
      setStep(1);
      setSearchSource('');
      setSourceResults([]);
      setSourcePatient(null);
      setSearchDest('');
      setDestResults([]);
      setDestinationPatient(null);
      setFinished(false);
      setStats({ appointments: 0, evolutions: 0 });
    }
  }, [isOpen]);

  useEffect(() => {
    if (step === 3 && sourcePatient && destinationPatient) {
      fetchSourceStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const fetchSourceStats = async () => {
    try {
      // Get evolutions count
      const { count: evCount, error: evErr } = await supabase
        .from('medical_evolutions')
        .select('*', { count: 'exact', head: true })
        .eq('patient_id', sourcePatient.id);

      if (evErr) throw evErr;

      // Get appointments count
      let appQuery = supabase
        .from('appointments')
        .select('*', { count: 'exact', head: true });

      if (sourcePatient.dni) {
        appQuery = appQuery.eq('patient_dni', sourcePatient.dni);
      } else {
        appQuery = appQuery.eq('patient_name', sourcePatient.full_name);
      }

      const { count: appCount, error: appErr } = await appQuery;
      if (appErr) throw appErr;

      setStats({
        evolutions: evCount || 0,
        appointments: appCount || 0
      });
    } catch (err) {
      console.error('Error fetching source stats:', err);
    }
  };

  if (!isOpen) return null;

  const handleSearchSource = async (e) => {
    if (e) e.preventDefault();
    if (!searchSource.trim()) return;
    setLoadingSearch(true);
    try {
      const { data, error } = await supabase
        .from('patients')
        .select('*')
        .or(`full_name.ilike.%${searchSource}%,dni.ilike.%${searchSource}%`)
        .limit(10);
      if (error) throw error;
      setSourceResults(data || []);
    } catch (err) {
      addNotification('Error al buscar paciente origen', 'error');
    } finally {
      setLoadingSearch(false);
    }
  };

  const handleSearchDest = async (e) => {
    if (e) e.preventDefault();
    if (!searchDest.trim()) return;
    setLoadingSearch(true);
    try {
      const { data, error } = await supabase
        .from('patients')
        .select('*')
        .or(`full_name.ilike.%${searchDest}%,dni.ilike.%${searchDest}%`)
        .neq('id', sourcePatient.id) // Exclude source patient
        .limit(10);
      if (error) throw error;
      setDestResults(data || []);
    } catch (err) {
      addNotification('Error al buscar paciente destino', 'error');
    } finally {
      setLoadingSearch(false);
    }
  };

  const executeMerge = async () => {
    if (!sourcePatient || !destinationPatient) return;
    setMerging(true);
    try {
      // 1. Prepare fields to patch on Destination Patient (smart merge empty fields)
      const patchData = {};
      if (!destinationPatient.dni && sourcePatient.dni) patchData.dni = sourcePatient.dni;
      if (!destinationPatient.phone && sourcePatient.phone) patchData.phone = sourcePatient.phone;
      if (!destinationPatient.email && sourcePatient.email) patchData.email = sourcePatient.email;
      if (!destinationPatient.birthdate && sourcePatient.birthdate) patchData.birthdate = sourcePatient.birthdate;
      if (!destinationPatient.insurance_provider && sourcePatient.insurance_provider) patchData.insurance_provider = sourcePatient.insurance_provider;
      
      // Concatenate medical notes if both exist
      if (sourcePatient.medical_notes) {
        patchData.medical_notes = destinationPatient.medical_notes
          ? `${destinationPatient.medical_notes}\n\n[Nota importada por fusión]: ${sourcePatient.medical_notes}`
          : sourcePatient.medical_notes;
      }

      if (Object.keys(patchData).length > 0) {
        const { error: patchError } = await supabase
          .from('patients')
          .update(patchData)
          .eq('id', destinationPatient.id);
        
        if (patchError) throw patchError;
      }

      // 2. Transfer Medical Evolutions
      const { error: evTransferError } = await supabase
        .from('medical_evolutions')
        .update({ patient_id: destinationPatient.id })
        .eq('patient_id', sourcePatient.id);

      if (evTransferError) throw evTransferError;

      // 3. Transfer Appointments
      let appUpdateQuery = supabase.from('appointments').update({
        patient_dni: destinationPatient.dni || null,
        patient_name: destinationPatient.full_name,
        patient_email: destinationPatient.email || null,
        patient_phone: destinationPatient.phone || null
      });

      if (sourcePatient.dni) {
        appUpdateQuery = appUpdateQuery.eq('patient_dni', sourcePatient.dni);
      } else {
        appUpdateQuery = appUpdateQuery.eq('patient_name', sourcePatient.full_name);
      }

      const { error: appTransferError } = await appUpdateQuery;
      if (appTransferError) throw appTransferError;

      // 4. Delete Source Patient
      const { error: deleteError } = await supabase
        .from('patients')
        .delete()
        .eq('id', sourcePatient.id);

      if (deleteError) throw deleteError;

      // 5. Complete wizard
      setFinished(true);
      addNotification('Fusión completada con éxito', 'success');
      if (onMergeSuccess) onMergeSuccess();
    } catch (err) {
      console.error(err);
      addNotification('Error al fusionar pacientes: ' + err.message, 'error');
    } finally {
      setMerging(false);
    }
  };

  return (
    <div className="merge-overlay" onClick={onClose}>
      <div className="merge-container glass-modal-premium" onClick={e => e.stopPropagation()}>
        
        {/* Header */}
        <div className="merge-header">
          <div className="title-wrapper">
            <Users size={20} className="icon-green" />
            <h2>Unificar Pacientes Repetidos</h2>
          </div>
          <button className="btn-close-p" onClick={onClose}><X size={24} /></button>
        </div>

        {/* Steps Indicator */}
        {!finished && (
          <div className="merge-steps-bar">
            <div className={`step-indicator ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
              <div className="circle">1</div>
              <span>Paciente Duplicado</span>
            </div>
            <div className="line"></div>
            <div className={`step-indicator ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
              <div className="circle">2</div>
              <span>Paciente Principal</span>
            </div>
            <div className="line"></div>
            <div className={`step-indicator ${step >= 3 ? 'active' : ''}`}>
              <div className="circle">3</div>
              <span>Confirmar Fusión</span>
            </div>
          </div>
        )}

        {/* Body content */}
        <div className="merge-body">
          {finished ? (
            <div className="merge-success-state">
              <div className="success-circle">
                <Check size={48} />
              </div>
              <h3>¡Fusión Completada Exitosamente!</h3>
              <p>
                Los registros de <strong>{sourcePatient?.full_name?.toUpperCase()}</strong> se han unificado en la ficha de <strong>{destinationPatient?.full_name?.toUpperCase()}</strong>.
              </p>
              <span className="success-subtext">La ficha duplicada ha sido eliminada y todas las notas clínicas y turnos han sido reasignados de forma segura.</span>
              <button className="btn-close-wizard" onClick={onClose}>CERRAR ASISTENTE</button>
            </div>
          ) : (
            <>
              {/* STEP 1: Select Source Patient (Duplicate to be deleted) */}
              {step === 1 && (
                <div className="wizard-step">
                  <div className="step-instructions">
                    <h3>Paso 1: Selecciona el Paciente Duplicado (Se eliminará)</h3>
                    <p>Busca y selecciona la ficha que contiene datos duplicados o incorrectos. Esta ficha **desaparecerá** y sus registros serán transferidos al paciente principal.</p>
                  </div>

                  <form onSubmit={handleSearchSource} className="wizard-search-form">
                    <div className="search-input-wrapper">
                      <input 
                        type="text" 
                        placeholder="Buscar por Nombre o DNI de la ficha duplicada..."
                        value={searchSource}
                        onChange={(e) => setSearchSource(e.target.value)}
                        autoFocus
                      />
                      <button type="submit" className="btn-search-trigger" disabled={loadingSearch || !searchSource.trim()}>
                        {loadingSearch ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
                      </button>
                    </div>
                  </form>

                  {sourcePatient ? (
                    <div className="selected-patient-preview source card">
                      <div className="preview-badge red">FICHA DUPLICADA SELECCIONADA</div>
                      <h4>{sourcePatient.full_name?.toUpperCase()}</h4>
                      <p>DNI: {sourcePatient.dni || 'Sin Documento'} | Celular: {sourcePatient.phone || 'Sin Celular'}</p>
                      <button className="btn-change-selection" onClick={() => setSourcePatient(null)}>Cambiar Selección</button>
                    </div>
                  ) : (
                    <div className="search-results-box">
                      {sourceResults.length === 0 ? (
                        <div className="empty-results-text">Usa el buscador superior para encontrar pacientes.</div>
                      ) : (
                        <div className="search-list">
                          {sourceResults.map(p => (
                            <div key={p.id} className="search-list-item" onClick={() => setSourcePatient(p)}>
                              <span className="item-name">{p.full_name?.toUpperCase()}</span>
                              <span className="item-meta">DNI: {p.dni || '---'} | Cel: {p.phone || '---'}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="wizard-footer">
                    <button className="btn-cancel" onClick={onClose}>Cancelar</button>
                    <button 
                      className="btn-next" 
                      disabled={!sourcePatient} 
                      onClick={() => setStep(2)}
                    >
                      Continuar <ArrowRight size={16} />
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 2: Select Destination Patient (Main to keep) */}
              {step === 2 && (
                <div className="wizard-step">
                  <div className="step-instructions">
                    <h3>Paso 2: Selecciona el Paciente Principal (Se conservará)</h3>
                    <p>Busca y selecciona la ficha definitiva que deseas conservar. Todos los turnos e historias clínicas del duplicado se transferirán a este perfil.</p>
                  </div>

                  <form onSubmit={handleSearchDest} className="wizard-search-form">
                    <div className="search-input-wrapper">
                      <input 
                        type="text" 
                        placeholder="Buscar por Nombre o DNI del paciente definitivo..."
                        value={searchDest}
                        onChange={(e) => setSearchDest(e.target.value)}
                        autoFocus
                      />
                      <button type="submit" className="btn-search-trigger" disabled={loadingSearch || !searchDest.trim()}>
                        {loadingSearch ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
                      </button>
                    </div>
                  </form>

                  {destinationPatient ? (
                    <div className="selected-patient-preview destination card">
                      <div className="preview-badge green">FICHA PRINCIPAL SELECCIONADA</div>
                      <h4>{destinationPatient.full_name?.toUpperCase()}</h4>
                      <p>DNI: {destinationPatient.dni || 'Sin Documento'} | Celular: {destinationPatient.phone || 'Sin Celular'}</p>
                      <button className="btn-change-selection" onClick={() => setDestinationPatient(null)}>Cambiar Selección</button>
                    </div>
                  ) : (
                    <div className="search-results-box">
                      {destResults.length === 0 ? (
                        <div className="empty-results-text">Usa el buscador superior para encontrar pacientes.</div>
                      ) : (
                        <div className="search-list">
                          {destResults.map(p => (
                            <div key={p.id} className="search-list-item" onClick={() => setDestinationPatient(p)}>
                              <span className="item-name">{p.full_name?.toUpperCase()}</span>
                              <span className="item-meta">DNI: {p.dni || '---'} | Cel: {p.phone || '---'}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="wizard-footer">
                    <button className="btn-back" onClick={() => setStep(1)}>Volver</button>
                    <button 
                      className="btn-next" 
                      disabled={!destinationPatient} 
                      onClick={() => setStep(3)}
                    >
                      Continuar <ArrowRight size={16} />
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 3: Comparison and Final Confirmation */}
              {step === 3 && (
                <div className="wizard-step">
                  <div className="step-instructions">
                    <h3>Paso 3: Confirma la Fusión de Registros</h3>
                    <p>Por favor, revisa detalladamente la comparación de perfiles antes de ejecutar la fusión. Esta acción **no se puede deshacer**.</p>
                  </div>

                  <div className="merge-comparison-grid">
                    {/* Source column */}
                    <div className="comparison-col source card">
                      <span className="label-col red">Ficha a Eliminar</span>
                      <h4>{sourcePatient?.full_name?.toUpperCase()}</h4>
                      <div className="comparison-fields">
                        <div className="field-group">
                          <label>DNI</label>
                          <span>{sourcePatient?.dni || '---'}</span>
                        </div>
                        <div className="field-group">
                          <label>Celular</label>
                          <span>{sourcePatient?.phone || '---'}</span>
                        </div>
                        <div className="field-group">
                          <label>Email</label>
                          <span>{sourcePatient?.email || '---'}</span>
                        </div>
                        <div className="field-group">
                          <label>Obra Social</label>
                          <span>{sourcePatient?.insurance_provider || 'Particular'}</span>
                        </div>
                      </div>
                    </div>

                    <div className="comparison-arrow">
                      <ArrowRight size={32} />
                    </div>

                    {/* Destination column */}
                    <div className="comparison-col destination card">
                      <span className="label-col green">Ficha a Conservar</span>
                      <h4>{destinationPatient?.full_name?.toUpperCase()}</h4>
                      <div className="comparison-fields">
                        <div className="field-group">
                          <label>DNI</label>
                          <span>{destinationPatient?.dni || sourcePatient?.dni || '---'} {(!destinationPatient?.dni && sourcePatient?.dni) && <span className="merged-tag">(Se importará)</span>}</span>
                        </div>
                        <div className="field-group">
                          <label>Celular</label>
                          <span>{destinationPatient?.phone || sourcePatient?.phone || '---'} {(!destinationPatient?.phone && sourcePatient?.phone) && <span className="merged-tag">(Se importará)</span>}</span>
                        </div>
                        <div className="field-group">
                          <label>Email</label>
                          <span>{destinationPatient?.email || sourcePatient?.email || '---'} {(!destinationPatient?.email && sourcePatient?.email) && <span className="merged-tag">(Se importará)</span>}</span>
                        </div>
                        <div className="field-group">
                          <label>Obra Social</label>
                          <span>{destinationPatient?.insurance_provider || sourcePatient?.insurance_provider || 'Particular'} {(!destinationPatient?.insurance_provider && sourcePatient?.insurance_provider) && <span className="merged-tag">(Se importará)</span>}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Transfer Summary */}
                  <div className="transfer-summary-box card">
                    <h5>Resumen de Transferencia:</h5>
                    <ul>
                      <li>Se reasignarán <strong>{stats.evolutions}</strong> evoluciones clínicas al paciente definitivo.</li>
                      <li>Se reasignarán <strong>{stats.appointments}</strong> turnos históricos y futuros al paciente definitivo.</li>
                      {sourcePatient?.medical_notes && (
                        <li>Se concatenarán las observaciones y antecedentes clínicos de la ficha eliminada.</li>
                      )}
                    </ul>
                  </div>

                  {/* Warning Panel */}
                  <div className="merge-warning-panel">
                    <AlertTriangle size={20} />
                    <span>
                      Al hacer clic en "CONFIRMAR FUSIÓN", el paciente duplicado se eliminará de forma permanente.
                    </span>
                  </div>

                  <div className="wizard-footer">
                    <button className="btn-back" disabled={merging} onClick={() => setStep(2)}>Volver</button>
                    <button 
                      className="btn-confirm-merge" 
                      disabled={merging} 
                      onClick={executeMerge}
                    >
                      {merging ? (
                        <>
                          <Loader2 size={16} className="animate-spin" /> PROCESANDO FUSIÓN...
                        </>
                      ) : (
                        'CONFIRMAR FUSIÓN'
                      )}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MergePatientsModal;
